/**
 * Settlement Hook
 * 정산 페이지 데이터 관리 및 API 호출 로직
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAsync } from '../../../core/hooks/useAsync';
import { getSettlementData, getSupplierSettlementData } from '../api/settlementApi';
import { getPreAcctYn } from '../../home/delivery/api/deliveryApi';
import { getWithdrawalAccount } from '../withdrawal/api/withdrawalApi';
import { getUserInfo } from '../../../core/utils';
import {
    SettlementRequest,
    SettlementStats,
    DEFAULT_SETTLEMENT_STATS,
} from '../types/settlement';

/**
 * 주차 옵션 타입
 */
interface WeekOption {
    value: string;
    label: string;
    startDate: string; // YYYY-MM-DD
    endDate: string;   // YYYY-MM-DD
}

/**
 * 날짜 포맷 (MM.DD)
 */
const formatDateShort = (date: Date): string => {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}.${d}`;
};

/**
 * 날짜 포맷 (YYYY-MM-DD)
 */
const formatDateFull = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * 최근 8주차 옵션 생성 (수~화 기준) - 라이더용
 */
const generateRiderWeekOptions = (): WeekOption[] => {
    const now = new Date();
    const options: WeekOption[] = [];

    // 이번 주 수요일 찾기
    const dayOfWeek = now.getDay();
    const thisWednesday = new Date(now);

    // 현재 요일에서 수요일까지의 차이 계산
    if (dayOfWeek < 3) {
        // 일, 월, 화 -> 이전 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek + 4));
    } else {
        // 수, 목, 금, 토 -> 이번 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek - 3));
    }

    // 최근 8주차 생성 (현재 주 포함)
    for (let i = 0; i < 8; i++) {
        const startDate = new Date(thisWednesday);
        startDate.setDate(thisWednesday.getDate() - (i * 7));

        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6); // 화요일

        const startStr = formatDateShort(startDate);
        const endStr = formatDateShort(endDate);

        // 주차 번호 계산 (수요일 기준)
        const year = startDate.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const weekNumber = Math.ceil(((startDate.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);

        options.push({
            value: `rider-week-${i}`,
            label: `정산 일 (${startStr} ~ ${endStr})`,
            startDate: formatDateFull(startDate),
            endDate: formatDateFull(endDate),
        });
    }

    return options;
};

/**
 * 최근 8주차 옵션 생성 (수~화 기준) - 협력사용
 */
const generateWeekOptions = (): WeekOption[] => {
    const now = new Date();
    const options: WeekOption[] = [];

    // 이번 주 수요일 찾기
    const dayOfWeek = now.getDay();
    const thisWednesday = new Date(now);

    // 현재 요일에서 수요일까지의 차이 계산
    if (dayOfWeek < 3) {
        // 일, 월, 화 -> 이전 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek + 4));
    } else {
        // 수, 목, 금, 토 -> 이번 주 수요일
        thisWednesday.setDate(now.getDate() - (dayOfWeek - 3));
    }

    // 최근 8주차 생성 (현재 주 포함)
    for (let i = 0; i < 8; i++) {
        const startDate = new Date(thisWednesday);
        startDate.setDate(thisWednesday.getDate() - (i * 7));

        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6); // 화요일

        const startStr = formatDateShort(startDate);
        const endStr = formatDateShort(endDate);

        options.push({
            value: `week-${i}`,
            label: `정산 일 (${startStr} ~ ${endStr})`,
            startDate: formatDateFull(startDate),
            endDate: formatDateFull(endDate),
        });
    }

    return options;
};

/**
 * 정산 페이지 Hook
 */
export const useSettlement = () => {
    // 상태 관리
    const [settlementStats, setSettlementStats] = useState<SettlementStats>(DEFAULT_SETTLEMENT_STATS);
    const [supplierStats, setSupplierStats] = useState<any>(null); // 협력사 원본 데이터
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const userInfo = getUserInfo();
    const isSupplier = userInfo?.suppId;
    const [settlementType, setSettlementType] = useState<'weekly' | 'pre'>('weekly'); // 'weekly': 주정산, 'pre': 선정산
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
    const [withdrawalBalance, setWithdrawalBalance] = useState<number>(0);

    // 협력사용 주차 선택 상태
    const [weekOptions] = useState<WeekOption[]>(generateWeekOptions);
    const [selectedWeek, setSelectedWeek] = useState<string>('week-0'); // 기본: 이번 주
    
    // 라이더용 주차 선택 상태
    const [riderWeekOptions] = useState<WeekOption[]>(generateRiderWeekOptions);
    const [selectedRiderWeek, setSelectedRiderWeek] = useState<string>('rider-week-0'); // 기본: 이번 주
    
    // API 호출 상태 관리
    const settlementDataState = useAsync(getSettlementData);
    const preAcctYnState = useAsync(getPreAcctYn);

    // 날짜 변경 추적을 위한 ref
    const lastChangedRef = useRef<'from' | 'to' | null>(null);

    // 선택된 날짜를 YYYYMMDD 형식으로 변환
    const fromDateYyyymmdd = useMemo(() => fromDate.replace(/-/g, ''), [fromDate]);
    const toDateYyyymmdd = useMemo(() => toDate.replace(/-/g, ''), [toDate]);

    // 내부 공통 포맷터: Date -> YYYY-MM-DD
    const formatDateYmd = useCallback((date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    // 내부 공통 파서: YYYY-MM-DD -> Date
    const parseDateYmd = useCallback((ymd: string) => {
        const parts = ymd.split('-');
        if (parts.length !== 3) return new Date(NaN);
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-based month
        const day = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }, []);

    // 주정산 모드 감지
    const isWeeklySettlement = useCallback(() => {
        return settlementType === 'weekly';
    }, [settlementType]);

    // 수요일만 선택 가능하도록 날짜 범위 계산
    const getWednesdayOnlyDateRange = useCallback(() => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        
        // 6개월 전부터 시작 (과거 수요일들도 선택 가능)
        const sixMonthsAgo = new Date(currentYear, currentMonth - 6, 1);
        const firstWednesday = new Date(sixMonthsAgo);
        const dayOfWeek = sixMonthsAgo.getDay();
        const daysToAdd = dayOfWeek <= 3 ? (3 - dayOfWeek) : (10 - dayOfWeek);
        firstWednesday.setDate(sixMonthsAgo.getDate() + daysToAdd);
        
        // 6개월 후까지 (미래 수요일들도 선택 가능)
        const sixMonthsLater = new Date(currentYear, currentMonth + 6, 1);
        const lastWednesday = new Date(sixMonthsLater);
        const lastDayOfWeek = sixMonthsLater.getDay();
        const daysToSubtract = lastDayOfWeek >= 3 ? (lastDayOfWeek - 3) : (lastDayOfWeek + 4);
        lastWednesday.setDate(sixMonthsLater.getDate() - daysToSubtract);
        
        return {
            min: firstWednesday.toISOString().split('T')[0],
            max: lastWednesday.toISOString().split('T')[0]
        };
    }, []);

    /**
     * 협력사용 주차 선택 변경 핸들러
     */
    const handleWeekChange = useCallback((weekValue: string) => {
        setSelectedWeek(weekValue);
        const selectedOption = weekOptions.find(opt => opt.value === weekValue);
        if (selectedOption) {
            setFromDate(selectedOption.startDate);
            setToDate(selectedOption.endDate);
        }
    }, [weekOptions]);

    /**
     * 라이더용 주차 선택 변경 핸들러
     */
    const handleRiderWeekChange = useCallback((weekValue: string) => {
        setSelectedRiderWeek(weekValue);
        const selectedOption = riderWeekOptions.find(opt => opt.value === weekValue);
        if (selectedOption) {
            setFromDate(selectedOption.startDate);
            setToDate(selectedOption.endDate);
        }
    }, [riderWeekOptions]);

    /**
     * 정산 페이지 초기화 (초기 날짜 설정)
     */
    const initializeSettlement = useCallback(async () => {
        try {
            if (isSupplier && weekOptions.length > 0) {
                // 협력사: 첫 번째 주차 옵션(이번 주)으로 초기화
                const firstOption = weekOptions[0];
                setFromDate(firstOption.startDate);
                setToDate(firstOption.endDate);
                setSelectedWeek(firstOption.value);
            } else if (!isSupplier && riderWeekOptions.length > 0) {
                // 라이더: 첫 번째 주차 옵션(이번 주)으로 초기화
                const firstOption = riderWeekOptions[0];
                setFromDate(firstOption.startDate);
                setToDate(firstOption.endDate);
                setSelectedRiderWeek(firstOption.value);
            } else {
                // 기존 로직 - 가장 최근 수요일 계산 (fallback)
                const today = new Date();
                const dayOfWeek = today.getDay();
                const daysToSubtract = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
                const lastWednesday = new Date(today);
                lastWednesday.setDate(today.getDate() - daysToSubtract);
                const y = lastWednesday.getFullYear();
                const m = String(lastWednesday.getMonth() + 1).padStart(2, '0');
                const d = String(lastWednesday.getDate()).padStart(2, '0');
                const lastWednesdayStr = `${y}-${m}-${d}`;

                // 현재 일자 기준 가장 최신 수요일을 fromDate로 설정
                setFromDate(lastWednesdayStr);
            }

            setIsInitialized(true);
        } catch (err) {
            console.warn('Failed to initialize settlement:', err);
            setIsInitialized(true);
        }
    }, [isSupplier, weekOptions, riderWeekOptions]);

    // 출금가능 잔액 조회
    const fetchWithdrawalBalance = useCallback(async () => {
        try {
            const response = await getWithdrawalAccount();
            if (response.success && response.balAmt !== undefined) {
                setWithdrawalBalance(response.balAmt);
            }
        } catch (err) {
            console.warn('Failed to fetch withdrawal balance:', err);
        }
    }, []);

    // 컴포넌트 마운트 시 초기화 및 데이터 조회
    useEffect(() => {
        if (!isInitialized) {
            initializeSettlement();
        }
        fetchWithdrawalBalance();
    }, [isInitialized, initializeSettlement, fetchWithdrawalBalance]);

    // 초기화 완료 후 첫 데이터 조회
    useEffect(() => {
        if (isInitialized && fromDateYyyymmdd && toDateYyyymmdd) {
            fetchSettlementData();
        }
    }, [isInitialized]);

    // fromDate가 설정되었을 때 toDate 자동 설정
    useEffect(() => {
        if (fromDate && !toDate) {
            const start = parseDateYmd(fromDate);
            if (!isNaN(start.getTime())) {
                // 주정산/선정산 모두 fromDate + 6일 (7일치)
                const end = new Date(start);
                end.setDate(start.getDate() + 6);
                const expectedToDate = formatDateYmd(end);
                setToDate(expectedToDate);
            }
        }
    }, [fromDate, toDate, parseDateYmd, formatDateYmd]);

    // 단일 동기화 이펙트: 마지막 변경 필드 기준으로 반대편 값을 맞춤
    useEffect(() => {
        if (!fromDate && !toDate) return;

        if (lastChangedRef.current === 'from') {
            // 선정산인 경우 fromDate 변경 시 toDate를 자동으로 조정하지 않음
            if (settlementType === 'pre') {
                lastChangedRef.current = null;
                return;
            }
            
            // 주정산인 경우에만 fromDate 변경 시 toDate를 자동으로 조정
            const start = parseDateYmd(fromDate);
            if (isNaN(start.getTime())) return;
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            const expectedToDate = formatDateYmd(end);
            if (toDate !== expectedToDate) {
                setToDate(expectedToDate);
            }
            lastChangedRef.current = null;
            return;
        }

        if (lastChangedRef.current === 'to') {
            // 선정산인 경우 toDate 변경 시 fromDate를 자동으로 조정하지 않음
            if (settlementType === 'pre') {
                lastChangedRef.current = null;
                return;
            }
            
            // 주정산인 경우에만 toDate 변경 시 fromDate를 자동으로 조정
            const end = parseDateYmd(toDate);
            if (isNaN(end.getTime())) return;
            const start = new Date(end);
            start.setDate(end.getDate() - 6);
            const expectedFromDate = formatDateYmd(start);
            if (fromDate !== expectedFromDate) {
                setFromDate(expectedFromDate);
            }
            lastChangedRef.current = null;
            return;
        }
    }, [fromDate, toDate, formatDateYmd, parseDateYmd, settlementType]);

    /**
     * 정산 데이터 조회
     */
    const fetchSettlementData = useCallback(async () => {
        if (!fromDateYyyymmdd || !toDateYyyymmdd) return;

        try {
            let responseData;
            
            if (isSupplier) {
                // 협력사: supplier-settlement API 호출
                responseData = await getSupplierSettlementData(fromDateYyyymmdd, toDateYyyymmdd);
            } else {
                // 라이더: 기존 settlement-list API 호출
                const requestParams: SettlementRequest = {
                    fromDate: fromDateYyyymmdd,
                    toDate: toDateYyyymmdd,
                    preAcctYn: settlementType === 'weekly' ? 'N' : 'Y',
                };
                responseData = await settlementDataState.execute(requestParams);
            }
            
            // API 응답 처리
            const summary = responseData.settlementSummary;
            if (summary) {
                setIsFirstLoad(false);
                if (isSupplier) {
                    // 협력사 원본 데이터 저장
                    setSupplierStats(summary);
                    // 화면 표시용 기본 stats도 설정
                    setSettlementStats({
                        totalSettlementAmount: summary.acctVal || 0,
                        withdrawalBalance: summary.totBalAmt || 0,
                        deliveryProcessFee: summary.deliveryVal || 0,
                        additionalPayment: summary.addAcctVal || 0,
                        completionCount: summary.procCnt || 0,
                        promotion: 0,
                        operationFee: summary.maintncVal || 0,
                        employmentInsurance: summary.supplyAmt || 0,
                        industrialAccidentInsurance: summary.taxVat || 0,
                        hourlyInsurance: summary.supplyPrice || 0,
                        withholdingTax: summary.manageFee || 0,
                        advanceDeduction: summary.deductVal || 0,
                        hdcDeduction: 0,
                        sdcDeduction: 0,
                        recoveryAmt: 0,
                        currentUnpaidAmt: 0,
                        recoverAmt: 0,
                        advancePayment: summary.paymentMgmtFee || 0,
                    });
                } else {
                    // 라이더 데이터 매핑
                    setSettlementStats({
                        totalSettlementAmount: summary.totAcctVal || 0,
                        withdrawalBalance: summary.totBalAmt || 0,
                        deliveryProcessFee: summary.totDeliveryVal || 0,
                        additionalPayment: summary.totAddVal || 0,
                        completionCount: summary.totDeliveryCnt || 0,
                        promotion: summary.promotion || 0,
                        operationFee: summary.totManageVal || 0,
                        employmentInsurance: summary.totRiderEmpVal || 0,
                        industrialAccidentInsurance: summary.totRiderAccidtVal || 0,
                        hourlyInsurance: summary.totTimeInsurVal || 0,
                        withholdingTax: summary.totWithholdTax || 0,
                        advanceDeduction: summary.totDeductVal || 0,
                        hdcDeduction: summary.hdcDeductVal || 0,
                        sdcDeduction: summary.sdcDeductVal || 0,
                        recoveryAmt: summary.unpaidAmt || 0,
                        currentUnpaidAmt: summary.currentUnpaidAmt || 0,
                        recoverAmt: summary.totRecoverAmt || 0,
                        advancePayment: summary.totPreAcctVal || 0,
                    });
                }
            } else {
                setSettlementStats(DEFAULT_SETTLEMENT_STATS);
            }
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Failed to fetch settlement data:', err);
            }
            setSettlementStats(DEFAULT_SETTLEMENT_STATS);
        }
    }, [fromDateYyyymmdd, toDateYyyymmdd, settlementType, isSupplier]);

    /**
     * 날짜 변경 핸들러
     */
    const handleFromDateChange = useCallback((date: string) => {
        lastChangedRef.current = 'from';
        setFromDate(date);
    }, []);

    const handleToDateChange = useCallback((date: string) => {
        lastChangedRef.current = 'to';
        setToDate(date);
    }, []);


    /**
     * 데이터 새로고침
     */
    const refreshData = useCallback(() => {
        fetchSettlementData();
    }, [fetchSettlementData]);

    // 날짜나 정산 타입이 변경될 때마다 데이터 조회 (디바운스 추가)
    useEffect(() => {
        if (fromDateYyyymmdd && toDateYyyymmdd && lastChangedRef.current === null) {
            const timer = setTimeout(() => {
                fetchSettlementData();
            }, 300); // 300ms 디바운스
            
            return () => clearTimeout(timer);
        }
    }, [fromDateYyyymmdd, toDateYyyymmdd, settlementType, fetchSettlementData]);

    return {
        // 상태
        settlementStats: {
            ...settlementStats,
            withdrawalBalance,
        },
        supplierStats,
        fromDate,
        toDate,
        settlementType,

        // 협력사용 주차 선택 상태
        weekOptions,
        selectedWeek,

        // 라이더용 주차 선택 상태
        riderWeekOptions,
        selectedRiderWeek,

        // API 상태
        isLoading: settlementDataState.isLoading || preAcctYnState.isLoading || isFirstLoad,
        error: settlementDataState.error || preAcctYnState.error,
        isInitialized,

        // 액션
        handleFromDateChange,
        handleToDateChange,
        handleSettlementTypeChange: isSupplier ? (() => { /* 협력사는 타입 변경 불가 */ }) : setSettlementType,
        handleWeekChange,
        handleRiderWeekChange,
        refreshData,

        // 헬퍼
        isWeeklySettlement,
        getWednesdayOnlyDateRange,
        isDataLoaded: settlementStats.totalSettlementAmount > 0,
        isSupplier,
    };
};
